import { userSegRedirectEntrypoint } from '@jira/redirect-entrypoints__user-seg-redirect/src/userSegRedirectEntrypoint.entrypoint.tsx';
import { MenuIdDashboards } from '@jira/navigation-apps__constants/src/MenuIdDashboards.tsx';
import { SidebarIdContent } from '@jira/navigation-apps__constants/src/SidebarIdContent.tsx';
import { dashboardBackgroundScriptsLayout } from '@jira/platform__layouts-background-scripts-layout/src/index.tsx';
import { DASHBOARD } from '@jira/platform__observability-capabilities/src/constants.tsx';
import { NORMAL_EARLY_ASSET_STRATEGY } from '@jira/platform__common-constants/src/ssr-early-assets.tsx';
import { globalLayout } from '@jira/platform__layouts-global-layout/src/index.tsx';
import { createEntry } from '@jira/platform__routing-route-entry/src/createEntry.tsx';
import { composeLayouts } from '@jira/platform__layouts-route-layout/src/GlobalComponent.tsx';
import { getNavigationResources } from '@jira/router-resources__navigation/src/getNavigationResources.tsx';
import { resourceWithCondition } from '@jira/router-resources__utils/src/resourceWithCondition.tsx';
import { capabilitiesConfigResource } from '@jira/router-resources__viz-platform/src/capabilitiesConfigResource.tsx';
import { dashboardLastVisitedRoute } from '@jira/router-routes__dashboard-routes/src/dashboardLastVisitedRoute.tsx';
import { dashboardLastVisitedEntryPoint } from '@jira/global-pages__dashboard-last-visited/src/dashboardLastVisitedEntryPoint.entrypoint.tsx';
import { createPageEntryPoint } from '@jira/platform__routing-create-route-entry-point/src/index.tsx';
import { nav4SidebarContentCoreEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-core/src/nav4SidebarContentCoreEntryPoint.entrypoint.tsx';
import { gsnSidebarContentEntryPoint } from '@jira/nav-platform-integration__sidebar/src/gsnSidebarContentEntryPoint.entrypoint.tsx';
import { nav4DashboardsContentEntryPoint } from '@jira/navigation-apps__sidebar-nav4-sidebars-content-dashboards/src/ui/dashboards/default/async-content/nav4DashboardsContentEntryPoint.entrypoint.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { Route } from '@atlassian/react-resource-router';
import {
	dashboardResources,
	LazyForPaintBackgroundScripts,
	LazyForPaintConnectJsApi,
} from './ui/index.tsx';

/**
 * Historically, /jira/dashboards/last-visited was grouped with dashboardViewRouteEntry,
 * Now, last-visited is separated into its own entry, but the configuration still mirrors dashboardViewRouteEntry
 * where `dashboardResources()` resolves to the last visited dashboard.
 */
export const dashboardLastVisitedRouteEntry: Route = createEntry(dashboardLastVisitedRoute, () => ({
	group: 'dashboard',
	entryPoint: () => (fg('navx-global-side-nav') ? GSNPageEntryPoint : pageEntryPoint),
	layout: composeLayouts(globalLayout, dashboardBackgroundScriptsLayout),

	navigation: {
		menuId: MenuIdDashboards,
		sidebarId: SidebarIdContent, // Nav4
	},

	resources: [
		...getNavigationResources(
			fg('navx-global-side-nav') ? { isUsingSidebarEntrypoint: true } : undefined,
		),
		...dashboardResources(),
		resourceWithCondition(
			() => expVal('platform_avp_jd_merged_directory_afm', 'isEnabled', false),
			capabilitiesConfigResource,
		),
	],

	forPaint: [LazyForPaintBackgroundScripts, LazyForPaintConnectJsApi],

	meta: {
		capability: DASHBOARD,
	},

	earlyAssets: NORMAL_EARLY_ASSET_STRATEGY,
	ufoName: expVal('platform_avp_jd_merged_directory_afm', 'isEnabled', false)
		? 'dashboard-last-visited'
		: 'dashboard',
	supportsPreviewPanel: true,
	wrmMode: 'early',

	bifrost: {
		bifrostGroup: 'shell-only',
	},
}));

const pageEntryPoint = createPageEntryPoint({
	main: dashboardLastVisitedEntryPoint,
	leftSidebar: {
		sidebar: nav4SidebarContentCoreEntryPoint,
		entryPoints: { dashboards: { entryPoint: nav4DashboardsContentEntryPoint } },
	},
	redirect: userSegRedirectEntrypoint,
});

const GSNPageEntryPoint = createPageEntryPoint({
	main: dashboardLastVisitedEntryPoint,
	leftSidebar: {
		sidebar: gsnSidebarContentEntryPoint,
		entryPoints: { dashboards: { entryPoint: nav4DashboardsContentEntryPoint } },
	},
	redirect: userSegRedirectEntrypoint,
});
